/**
 * Tool call → activity chip mapping, for the "Buscando…" chips the transcript renders while (or
 * after) the agent calls a SERVER tool (one that runs inside the Python ADK process, as opposed to
 * the browser-dispatched UI tools in `tools.ts`).
 *
 * This used to be three independent `if`/`switch` chains — one in the live streaming path
 * (`server/adk/runtime/remoteAgent.ts`), one reconstructing history on reload
 * (`app/components/ChatMessages.vue`'s `historicalActivity`), one for a subagent's own nested calls
 * (`app/composables/web-chat/normalizers.ts`'s `nestedToolCallLabel`) — kept in sync by hand. A new
 * tool (`search_workflows`, MAR-437) landed in none of them: it fell through every switch's generic
 * default, and because the group-summary UI (`ToolActivityList.vue`) had NO way to tell what kind of
 * search it was — only that it had a `queries` array — it got grouped under a hardcoded "búsquedas en
 * la legislación" label that was true only for the ONE tool that shape used to belong to.
 *
 * One table now backs all three switches AND the grouping: `code` is what
 * `app/utils/activityStatusLabel.ts` turns into the per-call chip text (unchanged, per-tool, as
 * specific as before); `kind` is new — the domain the grouped summary label picks by.
 */

export type ToolActivityKind = 'legal' | 'workflow' | 'documents' | 'help' | 'generic'

export interface ToolActivityDefinition {
  /** i18n status code — passed to `activityStatusLabel()` for the per-call chip text. */
  code: string
  /** What domain this belongs to. Drives grouping and the collapsed summary label — never the chip
   *  text itself, which stays per-tool via `code`. */
  kind: ToolActivityKind
  /** English fallback for the wire `status` event's `message` field — only ever shown if a client
   *  somehow can't resolve `code` via i18n (`activityStatusLabel`'s own `default` branch). Not
   *  localized: it is a last resort, not a rendering path any user is expected to see. */
  message: string
}

/**
 * Keyed by the SERVER tool's real name (`function_call`'s `name`, or the persisted tool call's
 * `tc.name`) — never guessed from shape. Adding a new searchable server tool means adding one line
 * here; `tests/unit/toolActivityMap.test.ts` fails the build if a known server tool is missing.
 */
export const TOOL_ACTIVITY_MAP: Record<string, ToolActivityDefinition> = {
  quick_research: { code: 'legal_researching', kind: 'legal', message: 'Investigando legislación...' },
  lookup_legal_reference: { code: 'legal_reference_reading', kind: 'legal', message: 'Consultando referencia...' },
  query_legal_catalog: { code: 'legal_searching', kind: 'legal', message: 'Buscando legislación...' },
  read_legal_details: { code: 'legal_reading', kind: 'legal', message: 'Leyendo legislación...' },
  search_user_documents: { code: 'documents_searching', kind: 'documents', message: 'Buscando documentos...' },
  get_document_detail: { code: 'document_reading', kind: 'documents', message: 'Leyendo documento...' },
  get_app_help: { code: 'help_checking', kind: 'help', message: 'Consultando ayuda...' },
  search_workflows: { code: 'workflow_searching', kind: 'workflow', message: 'Buscando plantillas...' },
}

/** A tool with no entry above — never mislabeled into a domain it isn't, just generic. */
export const DEFAULT_TOOL_ACTIVITY: ToolActivityDefinition = { code: 'searching', kind: 'generic', message: 'Buscando...' }

export function toolActivityFor(toolName: string): ToolActivityDefinition {
  return TOOL_ACTIVITY_MAP[toolName] ?? DEFAULT_TOOL_ACTIVITY
}
